/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {
  FormEvent,
  MouseEvent,
  useCallback
} from 'react';
import isObject from 'lodash.isobject';
import {GenericObject} from '@ion/core/src/types/entities';
import {isFalse} from '@ion/core/src/utils/BooleanUtils';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import {BulkEditorType} from '../editors/BulkEditorUtils';
import {FormState} from './useFormState';

// defines the useFormSave prop type definitions ...
export type UseFormSaveProps = {
  formState: FormState;
  getFormState?: (formState: FormState) => FormState;
  setFormState: (item: GenericObject) => void;
  serialize: (state: FormState | BulkEditorType) => GenericObject | GenericObject[];
  validate?: GenericObject;
  onSave?: (item: GenericObject | GenericObject[]) => void;
};

/**
 * @useFormSave
 *
 * Provides a reusable hook which allows for automating the process of of managing an
 * editor save action / form submit.
 *
 * ```typescript
 *
 *   import {useFormSave} from '@ion/dls/src/hooks/';
 *
 *   const onSubmit = useFormSave({
 *     formState,
 *     setFormState,
 *     serialize,
 *     validate,
 *     onSave
 *   });
 *
 * ```
 *
 */
export const useFormSave = ({
  formState,
  getFormState,
  setFormState,
  serialize,
  validate,
  onSave
}: UseFormSaveProps) => (
  useCallback((e?: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>) => {
    const newFormState = getFormState?.(formState) || formState;
    e?.preventDefault?.();
    const errors = {};
    for (const [name, val] of Object.entries(newFormState)) {
      const validationResult = validate?.[name]?.(val);
      if (
        (val?.required && isEmpty(val.value)) || val?.error ||
        (validate?.[name] && (isFalse(validationResult) || isObject(validationResult)))
      ) {
        errors[name] = {
          ...val,
          error : true,
          value : isObject(validationResult) ? validationResult : val.value
        };
      }
    }
    if (isObjectEmpty(errors)) {
      const serialized = serialize(newFormState);
      onSave?.(serialized);
    } else {
      setFormState(errors);
    }
  }, [formState, getFormState, setFormState, onSave, serialize])
);

export default useFormSave;
